'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
type ReviewerRole = 'client' | 'consultant';
type RatingFilter = 'all' | '5' | '4' | '3' | '2' | '1';
type DateFilter = 'all' | 'today' | 'week' | 'month' | '3months';
type SessionTypeFilter = 'all' | 'client' | 'consultant';

interface ReviewUser {
  id: string;
  full_name: string;
}

interface SessionReview {
  id: string;
  booking_id: string | null;
  reviewer_id: string;
  reviewee_id: string;
  reviewer_role: ReviewerRole;
  rating: number;
  feedback: string | null;
  is_visible_on_profile: boolean;
  created_at: string;
  reviewer?: ReviewUser | null;
  reviewee?: ReviewUser | null;
}

interface RatingSummary {
  average: number;
  total: number;
  distribution: Record<number, number>;
}

// ─── Star Rating (readonly) ────────────────────────────────────────────────────
function StarRating({ value, size = 'md' }: { value: number; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClass = size === 'sm' ? 'w-3.5 h-3.5' : size === 'lg' ? 'w-7 h-7' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={sizeClass}
          viewBox="0 0 20 20"
          fill={value >= star ? '#f59e0b' : 'none'}
          stroke={value >= star ? '#f59e0b' : '#d1d5db'}
          strokeWidth="1.5"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

// ─── Rating Summary Cards ──────────────────────────────────────────────────────
function RatingSummarySection({ summary }: { summary: RatingSummary }) {
  const pct = (count: number) => summary.total > 0 ? Math.round((count / summary.total) * 100) : 0;

  const summaryCards = [
    {
      label: 'Average Rating',
      value: summary.average.toFixed(1),
      sub: `out of 5.0`,
      icon: 'StarIcon',
      color: 'text-amber-500',
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      border: 'border-amber-200 dark:border-amber-800',
    },
    {
      label: 'Total Reviews',
      value: summary.total.toString(),
      sub: 'all time',
      icon: 'ChatBubbleLeftEllipsisIcon',
      color: 'text-primary',
      bg: 'bg-primary/5',
      border: 'border-primary/20',
    },
    {
      label: '5-Star Reviews',
      value: (summary.distribution[5] ?? 0).toString(),
      sub: `${pct(summary.distribution[5] ?? 0)}% of total`,
      icon: 'HandThumbUpIcon',
      color: 'text-emerald-600',
      bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      border: 'border-emerald-200 dark:border-emerald-800',
    },
    {
      label: 'Satisfaction Rate',
      value: `${pct((summary.distribution[4] ?? 0) + (summary.distribution[5] ?? 0))}%`,
      sub: '4★ and above',
      icon: 'CheckBadgeIcon',
      color: 'text-teal-600',
      bg: 'bg-teal-50 dark:bg-teal-900/20',
      border: 'border-teal-200 dark:border-teal-800',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card) => (
          <div
            key={card.label}
            className={`${card.bg} border ${card.border} rounded-2xl p-4 flex flex-col gap-2`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-500 text-muted-foreground">{card.label}</span>
              <Icon name={card.icon as Parameters<typeof Icon>[0]['name']} size={16} className={card.color} />
            </div>
            <p className={`text-2xl font-700 ${card.color}`}>{card.value}</p>
            <p className="text-xs text-muted-foreground">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Rating Distribution Bar */}
      <div className="bg-card border border-border rounded-2xl p-5 shadow-card">
        <h3 className="text-sm font-600 text-foreground mb-4">Rating Distribution</h3>
        <div className="space-y-2.5">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = summary.distribution[star] ?? 0;
            const percentage = pct(count);
            return (
              <div key={star} className="flex items-center gap-3">
                <div className="flex items-center gap-1 w-12 flex-shrink-0">
                  <span className="text-xs font-500 text-foreground">{star}</span>
                  <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1.5">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
                <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-amber-400 transition-all duration-500"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground w-16 text-right flex-shrink-0">
                  {count} ({percentage}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Review Card ───────────────────────────────────────────────────────────────
function ReviewCard({ review }: { review: SessionReview }) {
  const reviewerName = review.reviewer?.full_name ?? 'Anonymous';
  const revieweeName = review.reviewee?.full_name ?? 'Unknown';
  const initials = reviewerName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  const dateStr = new Date(review.created_at).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  const roleBg = review.reviewer_role === 'client' ?'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
  const roleLabel = review.reviewer_role === 'client' ? 'Client Review' : 'Consultant Review';

  return (
    <div className="bg-card border border-border rounded-2xl p-5 shadow-card hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full gradient-teal flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-600 text-foreground truncate">{reviewerName}</p>
            <p className="text-xs text-muted-foreground">reviewed {revieweeName}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${roleBg}`}>{roleLabel}</span>
          <span className="text-xs text-muted-foreground">{dateStr}</span>
        </div>
      </div>

      <StarRating value={review.rating} size="sm" />

      {review.feedback && (
        <p className="mt-3 text-sm text-foreground/80 leading-relaxed border-l-2 border-primary/30 pl-3">
          &ldquo;{review.feedback}&rdquo;
        </p>
      )}

      {!review.is_visible_on_profile && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Icon name="EyeSlashIcon" size={13} />
          <span>Hidden from profile</span>
        </div>
      )}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function ReviewSkeleton() {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 animate-pulse space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-muted" />
        <div className="space-y-1.5 flex-1">
          <div className="h-3.5 bg-muted rounded w-1/3" />
          <div className="h-3 bg-muted rounded w-1/4" />
        </div>
      </div>
      <div className="h-3 bg-muted rounded w-24" />
      <div className="h-3 bg-muted rounded w-full" />
      <div className="h-3 bg-muted rounded w-4/5" />
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function ConsultantReviewsPage() {
  const supabase = createClient();

  const [reviews, setReviews] = useState<SessionReview[]>([]);
  const [summary, setSummary] = useState<RatingSummary>({ average: 0, total: 0, distribution: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [ratingFilter, setRatingFilter] = useState<RatingFilter>('all');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [sessionTypeFilter, setSessionTypeFilter] = useState<SessionTypeFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('session_reviews')
        .select('id, booking_id, reviewer_id, reviewee_id, reviewer_role, rating, feedback, is_visible_on_profile, created_at')
        .order('created_at', { ascending: false });

      // Rating filter
      if (ratingFilter !== 'all') {
        query = query.eq('rating', parseInt(ratingFilter));
      }

      // Date filter
      if (dateFilter !== 'all') {
        const now = new Date();
        let from: Date;
        if (dateFilter === 'today') {
          from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (dateFilter === 'week') {
          from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        } else if (dateFilter === 'month') {
          from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        } else {
          from = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        }
        query = query.gte('created_at', from.toISOString());
      }

      // Session type filter
      if (sessionTypeFilter !== 'all') {
        query = query.eq('reviewer_role', sessionTypeFilter);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      const rawReviews = data ?? [];

      // Fetch user profiles for reviewer/reviewee names
      const userIds = Array.from(new Set([
        ...rawReviews.map((r) => r.reviewer_id),
        ...rawReviews.map((r) => r.reviewee_id),
      ])).filter(Boolean);

      let userMap: Record<string, ReviewUser> = {};
      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .in('id', userIds);
        (users ?? []).forEach((u) => { userMap[u.id] = u; });
      }

      const enriched: SessionReview[] = rawReviews.map((r) => ({
        ...r,
        reviewer: userMap[r.reviewer_id] ?? null,
        reviewee: userMap[r.reviewee_id] ?? null,
      }));

      // Apply search filter client-side (on names/feedback)
      const filtered = searchQuery.trim()
        ? enriched.filter((r) => {
            const q = searchQuery.toLowerCase();
            return (
              r.reviewer?.full_name?.toLowerCase().includes(q) ||
              r.reviewee?.full_name?.toLowerCase().includes(q) ||
              r.feedback?.toLowerCase().includes(q)
            );
          })
        : enriched;

      setReviews(filtered);

      // Compute summary from ALL reviews (no filters) for accurate aggregation
      const { data: allData } = await supabase
        .from('session_reviews')
        .select('rating');

      const allRatings = allData ?? [];
      const total = allRatings.length;
      const avg = total > 0 ? allRatings.reduce((s, r) => s + r.rating, 0) / total : 0;
      const dist: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      allRatings.forEach((r) => { dist[r.rating] = (dist[r.rating] ?? 0) + 1; });
      setSummary({ average: avg, total, distribution: dist });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [ratingFilter, dateFilter, sessionTypeFilter, searchQuery]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const ratingOptions: { value: RatingFilter; label: string }[] = [
    { value: 'all', label: 'All Ratings' },
    { value: '5', label: '5 Stars' },
    { value: '4', label: '4 Stars' },
    { value: '3', label: '3 Stars' },
    { value: '2', label: '2 Stars' },
    { value: '1', label: '1 Star' },
  ];

  const dateOptions: { value: DateFilter; label: string }[] = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
    { value: '3months', label: 'Last 3 Months' },
  ];

  const sessionTypeOptions: { value: SessionTypeFilter; label: string }[] = [
    { value: 'all', label: 'All Types' },
    { value: 'client', label: 'Client Reviews' },
    { value: 'consultant', label: 'Consultant Reviews' },
  ];

  return (
    <AppLayout activeRoute="/consultant-reviews">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-700 text-foreground">Consultant Reviews</h1>
            <p className="text-sm text-muted-foreground mt-1">
              All session reviews across the platform — filtered by rating, date, and session type.
            </p>
          </div>
          <div className="flex items-center gap-2 bg-primary/10 text-primary px-3 py-2 rounded-xl">
            <Icon name="StarIcon" size={16} />
            <span className="text-sm font-600">{summary.average.toFixed(1)} avg</span>
          </div>
        </div>

        {/* Rating Summary */}
        <RatingSummarySection summary={summary} />

        {/* Filters */}
        <div className="bg-card border border-border rounded-2xl p-4 shadow-card space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="FunnelIcon" size={16} className="text-muted-foreground" />
            <span className="text-sm font-600 text-foreground">Filters</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Search */}
            <div className="relative">
              <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search reviews…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-muted border border-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>

            {/* Rating Filter */}
            <select
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value as RatingFilter)}
              className="bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {ratingOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {/* Date Filter */}
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as DateFilter)}
              className="bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {dateOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {/* Session Type Filter */}
            <select
              value={sessionTypeFilter}
              onChange={(e) => setSessionTypeFilter(e.target.value as SessionTypeFilter)}
              className="bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {sessionTypeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Active filter chips */}
          {(ratingFilter !== 'all' || dateFilter !== 'all' || sessionTypeFilter !== 'all' || searchQuery) && (
            <div className="flex flex-wrap gap-2 pt-1">
              {ratingFilter !== 'all' && (
                <span className="flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-500 px-2.5 py-1 rounded-full">
                  {ratingOptions.find((o) => o.value === ratingFilter)?.label}
                  <button onClick={() => setRatingFilter('all')} className="hover:text-primary/70">
                    <Icon name="XMarkIcon" size={12} />
                  </button>
                </span>
              )}
              {dateFilter !== 'all' && (
                <span className="flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-500 px-2.5 py-1 rounded-full">
                  {dateOptions.find((o) => o.value === dateFilter)?.label}
                  <button onClick={() => setDateFilter('all')} className="hover:text-primary/70">
                    <Icon name="XMarkIcon" size={12} />
                  </button>
                </span>
              )}
              {sessionTypeFilter !== 'all' && (
                <span className="flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-500 px-2.5 py-1 rounded-full">
                  {sessionTypeOptions.find((o) => o.value === sessionTypeFilter)?.label}
                  <button onClick={() => setSessionTypeFilter('all')} className="hover:text-primary/70">
                    <Icon name="XMarkIcon" size={12} />
                  </button>
                </span>
              )}
              {searchQuery && (
                <span className="flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-500 px-2.5 py-1 rounded-full">
                  &ldquo;{searchQuery}&rdquo;
                  <button onClick={() => setSearchQuery('')} className="hover:text-primary/70">
                    <Icon name="XMarkIcon" size={12} />
                  </button>
                </span>
              )}
              <button
                onClick={() => { setRatingFilter('all'); setDateFilter('all'); setSessionTypeFilter('all'); setSearchQuery(''); }}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Reviews List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-600 text-foreground">
              {loading ? 'Loading…' : `${reviews.length} review${reviews.length !== 1 ? 's' : ''} found`}
            </h2>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-4 flex items-center gap-3 text-sm text-red-700 dark:text-red-300 mb-4">
              <Icon name="ExclamationCircleIcon" size={18} />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <ReviewSkeleton key={i} />)}
            </div>
          ) : reviews.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl p-12 text-center">
              <Icon name="StarIcon" size={40} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-600 text-foreground mb-1">No reviews found</p>
              <p className="text-xs text-muted-foreground">Try adjusting your filters to see more results.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
